import { z } from 'zod';

import {
  PAYMENT_METHODS,
  currencySchema,
  paymentSchema,
  planIntervalSchema,
  planLimitsSchema,
  planSchema,
  slugSchema,
  subscriptionSchema,
  type SubscriptionStatus,
} from './admin.js';

/**
 * Billing manual (§8 del plan): add-ons, facturas con numeración anual, pagos con comprobante,
 * máquina de estados de la suscripción, uso del período y márgenes. La lógica pura (estados,
 * numeración, límites, líneas) vive acá para que la usen igual la API, el worker y los tests.
 */

const isoDate = z.string();
const dateOnly = z.iso.date();

// ------------------------------------------------------------------ máquina de estados

export const SUBSCRIPTION_EVENTS = [
  'payment_registered',
  'invoice_overdue',
  'grace_expired',
  'manual_suspend',
  'reactivate',
  'cancel',
] as const;
export type SubscriptionEvent = (typeof SUBSCRIPTION_EVENTS)[number];

/**
 * Transiciones válidas (§8.4). `null` = el evento no cambia nada en ese estado (no es error: un
 * pago en `active` es lo normal). Cancelada es terminal.
 */
const TRANSITIONS: Record<
  SubscriptionStatus,
  Partial<Record<SubscriptionEvent, SubscriptionStatus>>
> = {
  trialing: {
    payment_registered: 'active',
    invoice_overdue: 'past_due',
    manual_suspend: 'suspended',
    cancel: 'cancelled',
  },
  active: { invoice_overdue: 'past_due', manual_suspend: 'suspended', cancel: 'cancelled' },
  past_due: {
    payment_registered: 'active',
    grace_expired: 'suspended',
    manual_suspend: 'suspended',
    reactivate: 'active',
    cancel: 'cancelled',
  },
  suspended: { reactivate: 'active', cancel: 'cancelled' },
  cancelled: {},
};

export class InvalidTransitionError extends Error {
  constructor(
    readonly from: SubscriptionStatus,
    readonly event: SubscriptionEvent,
  ) {
    super(`Transición inválida: ${from} --${event}-->`);
    this.name = 'InvalidTransitionError';
  }
}

/** Estado siguiente, o el mismo si el evento no aplica. Lanza sólo en transiciones prohibidas. */
export function nextSubscriptionStatus(
  from: SubscriptionStatus,
  event: SubscriptionEvent,
): SubscriptionStatus {
  const to = TRANSITIONS[from][event];
  if (to) return to;
  // Eventos "informativos" que no cambian el estado.
  if (event === 'payment_registered' || event === 'invoice_overdue' || event === 'grace_expired') {
    return from;
  }
  throw new InvalidTransitionError(from, event);
}

export function canTransition(from: SubscriptionStatus, event: SubscriptionEvent): boolean {
  return TRANSITIONS[from][event] !== undefined;
}

/** Días de gracia en `past_due` antes de suspender (default del plan §8.4). */
export const DEFAULT_GRACE_DAYS = 15;

// ------------------------------------------------------------------ numeración

export const DEFAULT_INVOICE_PREFIX = 'ETW';

/** `ETW-2026-0001`. El correlativo es por prefijo y año, sin huecos entre facturas emitidas. */
export function formatInvoiceNumber(prefix: string, year: number, seq: number): string {
  if (!Number.isInteger(seq) || seq < 1) throw new Error('correlativo inválido');
  return `${prefix}-${String(year)}-${String(seq).padStart(4, '0')}`;
}

export function isDraftNumber(number: string): boolean {
  return number.startsWith('BORRADOR-');
}

// ------------------------------------------------------------------ límites y uso

export const LIMIT_KEYS = [
  'max_companies',
  'max_users',
  'max_instantly_connections',
  'leads_ingested_per_month',
  'leads_qualified_per_month',
  'emails_sent_per_month',
  'ai_budget_cents_per_month',
  'storage_mb',
] as const;
export type LimitKey = (typeof LIMIT_KEYS)[number];
export type Limits = Partial<Record<LimitKey, number | null>>;

export const LIMIT_LABELS: Record<LimitKey, string> = {
  max_companies: 'Empresas',
  max_users: 'Usuarios',
  max_instantly_connections: 'Conexiones de Instantly',
  leads_ingested_per_month: 'Leads encontrados / mes',
  leads_qualified_per_month: 'Leads cualificados / mes',
  emails_sent_per_month: 'Emails enviados / mes',
  ai_budget_cents_per_month: 'Presupuesto de IA / mes (cént.)',
  storage_mb: 'Almacenamiento (MB)',
};

/** Límite efectivo = plan + Σ(add-on × cantidad) + override. `null` = ilimitado (gana siempre). */
export function effectiveLimits(
  plan: Limits,
  addons: readonly { limitsDelta: Limits; quantity: number }[],
  override: Limits | null | undefined,
): Limits {
  const out: Limits = { ...plan };
  for (const a of addons) {
    for (const [k, delta] of Object.entries(a.limitsDelta) as [
      LimitKey,
      number | null | undefined,
    ][]) {
      if (delta === null || delta === undefined) continue;
      const base = out[k];
      if (base === null) continue; // ya es ilimitado
      out[k] = (base ?? 0) + delta * a.quantity;
    }
  }
  for (const [k, v] of Object.entries(override ?? {}) as [LimitKey, number | null | undefined][]) {
    if (v !== undefined) out[k] = v;
  }
  return out;
}

export const USAGE_METRICS = ['leads_ingested', 'leads_qualified', 'emails_sent'] as const;
export type UsageMetric = (typeof USAGE_METRICS)[number];

export const USAGE_METRIC_LIMIT: Record<UsageMetric, LimitKey> = {
  leads_ingested: 'leads_ingested_per_month',
  leads_qualified: 'leads_qualified_per_month',
  emails_sent: 'emails_sent_per_month',
};

export const USAGE_METRIC_LABELS: Record<UsageMetric, string> = {
  leads_ingested: 'Leads encontrados',
  leads_qualified: 'Leads cualificados',
  emails_sent: 'Emails enviados',
};

/** Nivel de uso (§8.3): soft al 80%/100%, gracia hasta 110%, hard por encima. */
export type UsageLevel = 'ok' | 'soft80' | 'soft100' | 'grace' | 'hard';
export function usageLevel(value: number, limit: number | null | undefined): UsageLevel {
  if (limit === null || limit === undefined) return 'ok';
  if (limit === 0) return value > 0 ? 'hard' : 'ok';
  const r = value / limit;
  if (r > 1.1) return 'hard';
  if (r > 1) return 'grace';
  if (r >= 1) return 'soft100';
  if (r >= 0.8) return 'soft80';
  return 'ok';
}

/** `YYYY-MM` de una fecha (UTC). */
export function periodOf(d: Date): string {
  return d.toISOString().slice(0, 7);
}

// ------------------------------------------------------------------ períodos y líneas

/** Siguiente período consecutivo al que termina en `end` (mismo intervalo). Fechas `YYYY-MM-DD`. */
export function nextPeriod(
  end: string,
  interval: 'month' | 'year',
): { start: string; end: string } {
  const start = addDays(end, 1);
  const [y, m, d] = start.split('-').map(Number) as [number, number, number];
  const endDate =
    interval === 'month'
      ? new Date(Date.UTC(y, m, d - 1))
      : new Date(Date.UTC(y + 1, m - 1, d - 1));
  return { start, end: endDate.toISOString().slice(0, 10) };
}

export function addDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export interface DraftLine {
  kind: 'plan' | 'addon' | 'overage' | 'adjustment';
  description: string;
  quantity: number;
  unitPriceCents: number;
  amountCents: number;
}

/** Líneas de la factura de un período: plan (o precio pactado) + add-ons activos. */
export function subscriptionLines(input: {
  planName: string;
  planPriceCents: number;
  customPriceCents: number | null;
  periodStart: string;
  periodEnd: string;
  addons: readonly { name: string; priceCents: number; quantity: number }[];
}): DraftLine[] {
  const unit = input.customPriceCents ?? input.planPriceCents;
  const lines: DraftLine[] = [
    {
      kind: 'plan',
      description: `Plan ${input.planName} (${input.periodStart} al ${input.periodEnd})${input.customPriceCents === null ? '' : ', precio pactado'}`,
      quantity: 1,
      unitPriceCents: unit,
      amountCents: unit,
    },
  ];
  for (const a of input.addons) {
    if (a.quantity <= 0) continue;
    lines.push({
      kind: 'addon',
      description: a.name,
      quantity: a.quantity,
      unitPriceCents: a.priceCents,
      amountCents: a.priceCents * a.quantity,
    });
  }
  return lines;
}

/** Totales de una factura con IVA en puntos básicos (2100 = 21%). */
export function invoiceTotals(
  lines: readonly { amountCents: number }[],
  taxRateBps: number,
): { subtotalCents: number; taxCents: number; totalCents: number } {
  const subtotalCents = lines.reduce((s, l) => s + l.amountCents, 0);
  const taxCents = Math.round((subtotalCents * taxRateBps) / 10_000);
  return { subtotalCents, taxCents, totalCents: subtotalCents + taxCents };
}

// ------------------------------------------------------------------ DTOs: datos de facturación

export const billingDetailsSchema = z.object({
  legalName: z.string().trim().max(200).optional(),
  taxId: z.string().trim().max(60).optional(),
  address: z.string().trim().max(500).optional(),
  email: z.string().trim().max(254).optional(),
  /** Sólo en la agencia: cómo pagar (IBAN/CBU/alias) — se muestra en las facturas y en Mi suscripción. */
  paymentInstructions: z.string().trim().max(1000).optional(),
  /** Sólo en la agencia: prefijo de la numeración (default ETW) e IVA en puntos básicos. */
  invoicePrefix: z
    .string()
    .regex(/^[A-Z0-9]{1,10}$/)
    .optional(),
  taxRateBps: z.number().int().min(0).max(10_000).optional(),
  dueDays: z.number().int().min(0).max(120).optional(),
});
export type BillingDetails = z.infer<typeof billingDetailsSchema>;

/** Costos estimados por empresa (margen del superadmin). Céntimos, admite decimales. */
export const costRatesSchema = z.object({
  lead_ingested_cents: z.number().min(0).max(100_000).optional(),
  lead_qualified_cents: z.number().min(0).max(100_000).optional(),
  email_sent_cents: z.number().min(0).max(100_000).optional(),
  fixed_monthly_cents: z.number().int().min(0).max(100_000_000).optional(),
  /** Costos de proveedores (estimados por unidad; ver PROVIDER_COSTS). */
  gemini_per_qualified_cents: z.number().min(0).max(100_000).optional(),
  leadmagic_per_pushed_cents: z.number().min(0).max(100_000).optional(),
  /**
   * `usd_fx` se retiró el 2026-10-07 (mismo día que se agregó, nunca se cargó): el costo real de IA
   * se muestra en USD, la moneda en la que factura Google, sin convertir (`ai_gemini`, Márgenes →
   * `aiUsdCents`). Con el modo "strip" de z.object una clave vieja se descarta sin error.
   */
  /**
   * `hunter_per_qualified_cents` y `apify_per_ingested_cents` se retiraron del contrato el
   * 2026-09-25 (Hunter y Apify/WF07 apagados; ninguna empresa tenía tarifa cargada —
   * verificado en producción). `costRatesSchema` es un `z.object` sin `.strict()`, así que su
   * modo por defecto ("strip") simplemente descarta esas claves si quedaran en un `cost_rates`
   * JSON viejo — no lanza error, no rompe el parse. No agregar de nuevo sin revisar CLAUDE.md /
   * docs/decisions.md 2026-09-25.
   */
});
export type CostRates = z.infer<typeof costRatesSchema>;

export const COST_RATE_OF: Record<UsageMetric, keyof CostRates> = {
  leads_ingested: 'lead_ingested_cents',
  leads_qualified: 'lead_qualified_cents',
  emails_sent: 'email_sent_cents',
};

/**
 * Costo estimado por proveedor (§8, F4a). Cada fila de `usage_counters` `est_<proveedor>` es
 * `unidades × tarifa de la empresa`, con las unidades derivadas de lo que la plataforma ya sabe:
 * Gemini por lead cualificado (extractor + cualificador + personalizador) y LeadMagic por lead
 * empujado a Instantly (se valida antes del push). Sin tarifa cargada no se escribe la fila.
 * Deliberadamente simple: es para ver el margen, no para conciliar con la factura del proveedor.
 *
 * Hunter (`est_hunter`) y Apify (`est_apify`) se sacaron el 2026-09-25 junto con sus tarifas en
 * `costRatesSchema` — ver el comentario ahí. Ninguna empresa tenía esas tarifas cargadas
 * (verificado en producción), así que `meterUsage` nunca había escrito filas `est_hunter` /
 * `est_apify` en `usage_counters`: sacarlas de acá no requiere borrar nada.
 */
export const PROVIDER_COSTS = [
  {
    // Sólo los leads SIN costo real registrado (cualificados antes del 2026-10-07 o por una copia
    // de WF02 sin el bloque @@AI_USAGE): los que lo tienen van a `ai_gemini`. Así no se cuenta dos veces.
    metric: 'est_gemini',
    rate: 'gemini_per_qualified_cents',
    base: 'leads_qualified_unmetered',
    label: 'Gemini estimado (por lead cualificado sin costo real)',
  },
  {
    metric: 'est_leadmagic',
    rate: 'leadmagic_per_pushed_cents',
    base: 'leads_pushed',
    label: 'LeadMagic (por lead empujado)',
  },
] as const satisfies readonly {
  metric: string;
  rate: keyof CostRates;
  base: UsageMetric | 'leads_pushed' | 'leads_qualified_unmetered';
  label: string;
}[];

// ------------------------------------------------------------------ costo real de IA (Gemini)

/**
 * Precio de lista de Gemini en USD por millón de tokens (tier pago, prompts ≤ 200k tokens: los de
 * WF02 rondan 2-8k). `in` incluye los resultados de búsqueda que inyecta el grounding y `out` el
 * razonamiento: así los registra el bloque @@AI_USAGE de los nodos de WF02 (decisión 2026-10-07).
 * La búsqueda de Google en sí es gratis hasta 1.500 prompts/día en 2.5, y WF02 no llega a eso.
 * Si Google cambia precios o se usa otro modelo, actualizar acá: un modelo sin precio se muestra
 * como «sin precio» y no suma costo (nunca se inventa).
 * Desde el 2026-10-20 WF02 usa Gemini 3.x (extractor y personalizador 3.5-flash-lite, cualificador
 * 3.8-flash). En 3.x la búsqueda se cobra por consulta ejecutada (5.000/mes gratis, luego 14 USD por
 * 1.000), no por prompt; con generateContent los 3.x casi nunca buscan, así que no se suma aparte.
 * ⚠️ gemini-3.8-flash sube a 1,50 / 7,50 el 2027-01-01: actualizar ese día.
 */
export const GEMINI_PRICES_USD_PER_MTOK: Readonly<Record<string, { in: number; out: number }>> = {
  'gemini-2.5-pro': { in: 1.25, out: 10 },
  'gemini-2.5-flash': { in: 0.3, out: 2.5 },
  'gemini-2.5-flash-lite': { in: 0.1, out: 0.4 },
  'gemini-3.5-flash-lite': { in: 0.3, out: 2.5 },
  'gemini-3.8-flash': { in: 0.75, out: 3.75 },
};

/** Lo que guarda WF02 en `full_result.ai_usage` (y la plataforma en `leads.qualification.ai_usage`). */
export const aiUsageSchema = z.record(
  z.string(),
  z.object({
    calls: z.number().optional(),
    in: z.number().optional(),
    out: z.number().optional(),
    searches: z.number().optional(),
  }),
);
export type AiUsage = z.infer<typeof aiUsageSchema>;

/** Costo en USD de un `ai_usage` (o de una suma por modelo). Los modelos sin precio van aparte. */
export function aiUsageCostUsd(usage: unknown): { usd: number; unpriced: string[] } {
  const parsed = aiUsageSchema.safeParse(usage);
  if (!parsed.success) return { usd: 0, unpriced: [] };
  let usd = 0;
  const unpriced: string[] = [];
  for (const [model, t] of Object.entries(parsed.data)) {
    const p = GEMINI_PRICES_USD_PER_MTOK[model];
    if (!p) {
      unpriced.push(model);
      continue;
    }
    usd += ((t.in ?? 0) * p.in + (t.out ?? 0) * p.out) / 1_000_000;
  }
  return { usd, unpriced };
}

// ------------------------------------------------------------------ DTOs: add-ons

export const planAddonSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  priceCents: z.number().int(),
  currency: currencySchema,
  interval: planIntervalSchema,
  limitsDelta: planLimitsSchema,
  archivedAt: isoDate.nullable(),
});
export type PlanAddon = z.infer<typeof planAddonSchema>;

export const createAddonSchema = z.object({
  code: slugSchema,
  name: z.string().trim().min(1).max(120),
  priceCents: z.number().int().nonnegative(),
  currency: currencySchema.default('EUR'),
  interval: planIntervalSchema.default('month'),
  limitsDelta: planLimitsSchema.default({}),
});
export const updateAddonSchema = createAddonSchema
  .omit({ code: true })
  .partial()
  .extend({ archived: z.boolean().optional() });

export const subscriptionAddonSchema = z.object({
  id: z.uuid(),
  subscriptionId: z.uuid(),
  addonId: z.uuid(),
  addonName: z.string(),
  quantity: z.number().int(),
  startedAt: isoDate,
  endedAt: isoDate.nullable(),
});
export type SubscriptionAddon = z.infer<typeof subscriptionAddonSchema>;

export const addSubscriptionAddonSchema = z.object({
  addonId: z.uuid(),
  quantity: z.number().int().min(1).max(1000).default(1),
});

export const subscriptionDetailSchema = subscriptionSchema.extend({
  graceDays: z.number().int(),
  pastDueSince: isoDate.nullable(),
  suspendedAt: isoDate.nullable(),
  /** Plan que entra al empezar el próximo período (cambio programado). */
  scheduledPlanId: z.uuid().nullable(),
  limitsOverride: planLimitsSchema.nullable(),
  addons: z.array(subscriptionAddonSchema),
});
export type SubscriptionDetail = z.infer<typeof subscriptionDetailSchema>;

export const subscriptionActionSchema = z.object({
  action: z.enum(['suspend', 'reactivate', 'cancel']),
  reason: z.string().trim().max(500).optional(),
});

export const changePlanSchema = z.object({
  planId: z.uuid(),
  /** `now`: desde hoy (sin prorrateo automático: se ajusta con una línea manual); `next`: desde el próximo período. */
  effective: z.enum(['now', 'next']).default('next'),
});

// ------------------------------------------------------------------ DTOs: facturas

export const INVOICE_STATUSES = ['draft', 'issued', 'paid', 'void', 'overdue'] as const;
export const invoiceStatusSchema = z.enum(INVOICE_STATUSES);
export type InvoiceStatus = z.infer<typeof invoiceStatusSchema>;

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: 'Borrador',
  issued: 'Emitida',
  paid: 'Pagada',
  void: 'Anulada',
  overdue: 'Vencida',
};

export const invoiceLineSchema = z.object({
  id: z.uuid(),
  kind: z.enum(['plan', 'addon', 'overage', 'adjustment']),
  description: z.string(),
  quantity: z.number().int(),
  unitPriceCents: z.number().int(),
  amountCents: z.number().int(),
  tenantId: z.uuid().nullable(),
});
export type InvoiceLine = z.infer<typeof invoiceLineSchema>;

export const invoiceSchema = z.object({
  id: z.uuid(),
  clientId: z.uuid(),
  clientName: z.string(),
  subscriptionId: z.uuid().nullable(),
  number: z.string(),
  status: invoiceStatusSchema,
  periodStart: isoDate.nullable(),
  periodEnd: isoDate.nullable(),
  currency: currencySchema,
  subtotalCents: z.number().int(),
  taxCents: z.number().int(),
  totalCents: z.number().int(),
  paidCents: z.number().int(),
  issuedAt: isoDate.nullable(),
  dueDate: isoDate.nullable(),
  paidAt: isoDate.nullable(),
  notes: z.string().nullable(),
  lines: z.array(invoiceLineSchema),
});
export type Invoice = z.infer<typeof invoiceSchema>;

export const invoicesQuerySchema = z.object({
  clientId: z.uuid().optional(),
  status: invoiceStatusSchema.optional(),
});

export const createInvoiceSchema = z.object({
  subscriptionId: z.uuid(),
  /** Default: el período actual de la suscripción. */
  periodStart: dateOnly.optional(),
  periodEnd: dateOnly.optional(),
  notes: z.string().max(2000).nullable().optional(),
});

export const invoiceLineInputSchema = z.object({
  kind: z.enum(['plan', 'addon', 'overage', 'adjustment']).default('adjustment'),
  description: z.string().trim().min(1).max(300),
  quantity: z.number().int().min(1).max(100_000).default(1),
  /** Puede ser negativo (descuento/ajuste). */
  unitPriceCents: z.number().int().min(-100_000_000).max(100_000_000),
  tenantId: z.uuid().nullable().optional(),
});

export const issueInvoiceSchema = z.object({
  /** IVA en puntos básicos; default el de la agencia (o 0). */
  taxRateBps: z.number().int().min(0).max(10_000).optional(),
  dueDays: z.number().int().min(0).max(120).optional(),
  /** Avisar por email a los client_admin del cliente. */
  notify: z.boolean().default(true),
});

export const markPaidSchema = z.object({
  reason: z.string().trim().min(3).max(500),
});

// ------------------------------------------------------------------ DTOs: pagos con comprobante

export const PROOF_MIMES = ['application/pdf', 'image/png', 'image/jpeg'] as const;
export const PROOF_MAX_BYTES = 10 * 1024 * 1024;

export const registerPaymentWithProofSchema = z.object({
  clientId: z.uuid(),
  invoiceId: z.uuid().nullable().optional(),
  amountCents: z.number().int().positive(),
  currency: currencySchema,
  method: z.enum(PAYMENT_METHODS),
  reference: z.string().max(200).nullable().optional(),
  receivedAt: dateOnly,
  notes: z.string().max(2000).nullable().optional(),
  proof: z
    .object({
      filename: z.string().trim().min(1).max(255),
      mime: z.enum(PROOF_MIMES),
      contentBase64: z.string().min(4),
    })
    .optional(),
});
export type RegisterPaymentWithProof = z.input<typeof registerPaymentWithProofSchema>;

export const paymentDetailSchema = paymentSchema.extend({
  notes: z.string().nullable(),
  hasProof: z.boolean(),
  proofFilename: z.string().nullable(),
});
export type PaymentDetail = z.infer<typeof paymentDetailSchema>;

export const billingEventSchema = z.object({
  id: z.uuid(),
  clientId: z.uuid(),
  subscriptionId: z.uuid().nullable(),
  invoiceId: z.uuid().nullable(),
  type: z.string(),
  data: z.record(z.string(), z.unknown()),
  at: isoDate,
});
export type BillingEvent = z.infer<typeof billingEventSchema>;

// ------------------------------------------------------------------ Mi suscripción (cliente)

export const usageRowSchema = z.object({
  metric: z.enum(USAGE_METRICS),
  label: z.string(),
  value: z.number(),
  limit: z.number().nullable(),
  level: z.enum(['ok', 'soft80', 'soft100', 'grace', 'hard']),
  byTenant: z.array(
    z.object({ tenantSlug: z.string(), tenantName: z.string(), value: z.number() }),
  ),
});
export type UsageRow = z.infer<typeof usageRowSchema>;

export const mySubscriptionSchema = z.object({
  clientId: z.uuid(),
  clientName: z.string(),
  subscription: subscriptionDetailSchema.nullable(),
  plan: planSchema.nullable(),
  limits: planLimitsSchema,
  period: z.string(),
  usage: z.array(usageRowSchema),
  counts: z.object({ companies: z.number(), users: z.number() }),
  invoices: z.array(invoiceSchema),
  payments: z.array(paymentDetailSchema),
  paymentInstructions: z.string().nullable(),
  entitlements: z.object({
    canIngest: z.boolean(),
    canQualify: z.boolean(),
    canPush: z.boolean(),
    reasons: z.array(z.string()),
  }),
});
export type MySubscription = z.infer<typeof mySubscriptionSchema>;

export const mySubscriptionResponseSchema = z.array(mySubscriptionSchema);

// ------------------------------------------------------------------ Márgenes (superadmin)

export const marginsQuerySchema = z.object({
  period: z
    .string()
    .regex(/^\d{4}-\d{2}$/)
    .optional(),
});

/**
 * Métricas de `usage_counters` cuyo `cost_cents` está en USD (la moneda en que factura el proveedor),
 * no en la moneda de facturación. No se suman al costo/margen en esa moneda ni se convierten: van
 * aparte (`aiUsdCents`) para que cada importe se muestre en su moneda.
 */
export const USD_COST_METRICS: readonly string[] = ['ai_gemini'];

export const marginRowSchema = z.object({
  clientId: z.uuid(),
  clientName: z.string(),
  currency: currencySchema.nullable(),
  revenueCents: z.number(),
  /** Costos en la moneda de facturación (tarifas cargadas a mano + fijo). Sin el costo real de IA. */
  costCents: z.number(),
  marginCents: z.number(),
  marginPct: z.number().nullable(),
  /** Costo real de IA del mes, en céntimos de USD, sin convertir. NO está restado del margen. */
  aiUsdCents: z.number(),
  tenants: z.array(
    z.object({
      tenantId: z.uuid(),
      tenantSlug: z.string(),
      tenantName: z.string(),
      costCents: z.number(),
      aiUsdCents: z.number(),
      usage: z.record(z.string(), z.number()),
    }),
  ),
});
export const marginsResponseSchema = z.object({
  period: z.string(),
  rows: z.array(marginRowSchema),
});
export type MarginsResponse = z.infer<typeof marginsResponseSchema>;

/** Costo real de IA por empresa en un mes (superadmin → Márgenes). Todo en USD, como factura Google. */
export const aiCostTenantSchema = z.object({
  tenantId: z.uuid(),
  tenantSlug: z.string(),
  tenantName: z.string(),
  /** Leads cualificados en el mes CON costo real registrado (los demás no entran en estos números). */
  leadsMetered: z.number(),
  /** Leads cualificados en el mes sin costo real (anteriores al registro): sólo informativo. */
  leadsUnmetered: z.number(),
  approved: z.number(),
  pushed: z.number(),
  costUsd: z.number(),
  perLeadUsd: z.number().nullable(),
  perApprovedUsd: z.number().nullable(),
  perPushedUsd: z.number().nullable(),
  /** Mes en curso: costo del mes al ritmo de los días con registro; meses cerrados: null. */
  projectedMonthUsd: z.number().nullable(),
  meteredSince: z.string().nullable(),
  models: z.array(
    z.object({
      model: z.string(),
      calls: z.number(),
      tokensIn: z.number(),
      tokensOut: z.number(),
      searches: z.number(),
      costUsd: z.number().nullable(),
    }),
  ),
});
export const aiCostsResponseSchema = z.object({
  period: z.string(),
  totalUsd: z.number(),
  projectedMonthUsd: z.number().nullable(),
  tenants: z.array(aiCostTenantSchema),
});
export type AiCostsResponse = z.infer<typeof aiCostsResponseSchema>;

// ------------------------------------------------------------------ Entitlements (motor)

export const entitlementsSchema = z.object({
  tenantId: z.uuid(),
  canIngest: z.boolean(),
  canQualify: z.boolean(),
  canPush: z.boolean(),
  /** Qué nunca se corta (§8.3): el motor lo lee para no frenar conversaciones en curso. */
  conversationsAllowed: z.literal(true),
  reasons: z.array(z.string()),
  subscriptionStatus: z.string().nullable(),
  features: z.record(z.string(), z.boolean()),
});
export type Entitlements = z.infer<typeof entitlementsSchema>;
